/**
 * Roles vigentes de un usuario, con alta y revocacion.
 */

import { useState } from 'react';
import { Button } from '@presentation/components/common';
import { useRoles, useUserRoles } from '@presentation/hooks';
import { RequireRole } from '@presentation/router/RequireRole';
import { SYSTEM_ROLES } from '@shared/constants';
import type { SsoUser } from '@domain/entities';
import styles from '../admin.module.css';

interface UserRolesPanelProps {
  user: SsoUser;
  onClose: () => void;
}

export function UserRolesPanel({ user, onClose }: UserRolesPanelProps) {
  const { roles: assigned, isLoading, error, assign, revoke } = useUserRoles(user.id);
  const { roles: available } = useRoles({ onlyActive: true });
  const [roleId, setRoleId] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  const assignable = available.filter(
    role => !assigned.some(current => current.roleId === role.id)
  );

  const handleAssign = async () => {
    if (!roleId) return;

    await assign(roleId, expiresAt ? new Date(expiresAt).toISOString() : undefined);
    setRoleId('');
    setExpiresAt('');
  };

  return (
    <section className={styles.panel}>
      <header className={styles.header}>
        <div>
          <h2 className={styles.title}>Roles de {user.fullName}</h2>
          <p className={styles.subtitle}>{user.email}</p>
        </div>
        <Button size="small" variant="secondary" onClick={onClose}>
          Cerrar
        </Button>
      </header>

      {error && <p className={styles.error}>{error}</p>}

      <RequireRole roles={[SYSTEM_ROLES.admin]}>
        <div className={styles.toolbar}>
          <select
            className={styles.select}
            value={roleId}
            onChange={event => setRoleId(event.target.value)}
            aria-label="Rol a asignar"
          >
            <option value="">Elegir rol...</option>
            {assignable.map(role => (
              <option key={role.id} value={role.id}>
                {role.code} — {role.name}
              </option>
            ))}
          </select>

          {/* Sin fecha elegida el navegador dibuja "dd/mm/aaaa" por su cuenta:
              `placeholder` no aplica a los campos de tipo date. La clase oculta
              ese texto mientras el campo este vacio y sin foco. */}
          <input
            className={`${styles.input} ${expiresAt ? '' : styles.dateVacio}`}
            type="date"
            value={expiresAt}
            onChange={event => setExpiresAt(event.target.value)}
            aria-label="Vencimiento (opcional)"
          />

          <Button size="small" onClick={() => void handleAssign()} disabled={!roleId}>
            Asignar
          </Button>
        </div>
      </RequireRole>

      {isLoading ? (
        <p className={styles.empty}>Cargando roles...</p>
      ) : assigned.length === 0 ? (
        <p className={styles.empty}>Sin roles asignados: no puede entrar a ninguna aplicacion.</p>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Rol</th>
              <th>Aplicacion</th>
              <th>Vence</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {assigned.map(role => (
              <tr key={role.roleId}>
                <td>
                  <code>{role.code}</code>
                </td>
                <td>{role.applicationCode ?? 'Global (SSO)'}</td>
                <td>{role.expiresAt ? new Date(role.expiresAt).toLocaleDateString() : '—'}</td>
                <td>
                  <RequireRole roles={[SYSTEM_ROLES.admin]}>
                    <Button size="small" variant="danger" onClick={() => void revoke(role.roleId)}>
                      Revocar
                    </Button>
                  </RequireRole>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
